import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import DeleteConfirmation from '../DeleteConfirmation/DeleteConfirmation';
import NotVioModal from '../NotVioModal/NotVioModal';
import moment from 'moment';
import { capitalizeFirstLetter } from '../../../helpers/utilities';
import { fetchManageNewsReport, manageNewsReport } from '../../Reducers/actions/newsActions';
import { fetchPostApi } from '../../../helpers/fetch';
import './ReportedNews.scss';

const fetchAdminDeleteNews = (news_id) => fetchPostApi(`/admin/news/${news_id}`, {}, 'DELETE');
const fetchAdminMarkNotViolating = (news_id) => fetchPostApi(`/admin/mark-safe-news/${news_id}`, {}, 'PUT');

const ReportedNews = () => {
    const dispatch = useDispatch();
    const newsReports = useSelector(state => state.news.newsReports || []);
    const loading = useSelector(state => state.news.loading);
    
    const [selected, setSelected] = useState(null);
    const [toRemove, setToRemove] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [isDeleting, setIsDeleting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const rowsPerPage = 10;

    const sortedData = useMemo(() => {
        let filteredReports = newsReports;
        
        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filteredReports = newsReports.filter(report => matchesSearchQuery(report, query));
        }
        
        // If no sorting is applied, return filtered data as is
        if (!sortConfig.key) {
            return filteredReports;
        }
        
        // Create a new array to avoid mutating the original
        const sortedReports = [...filteredReports].sort((a, b) => {
            const getSortValue = (report, key) => {
                switch (key) {
                    case "created_at":
                        return new Date(report[key]);
                    case "report_id":
                    case "reporter_id":
                    case "news_id":
                        return parseInt(report[key]) || 0;
                    case "news_title":
                        return (report.news?.title || '').toString().toLowerCase();
                    case "reporter_name":
                        return `${report.reporter?.firstName || ''} ${report.reporter?.lastName || ''}`.trim().toLowerCase();
                    default:
                        return (report[key] || '').toString().toLowerCase();
                }
            };

            const aValue = getSortValue(a, sortConfig.key);
            const bValue = getSortValue(b, sortConfig.key);

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        
        return sortedReports;
    }, [newsReports, sortConfig, searchQuery]);

    const paginatedData = useMemo(() => {
        return sortedData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
    }, [sortedData, currentPage]);

    const totalPages = useMemo(() => {
        return Math.ceil(sortedData.length / rowsPerPage);
    }, [sortedData.length]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'rejected': return 'fg-red';
            case 'accepted': return 'fg-green';
            case 'not_violating': return 'fg-green';
            case 'deleted': return 'fg-red';
            default: return 'fg-amber';
        }
    };

    const renderStatus = (status) => (
        <span className={`status-toggle ${status}`}>
            {capitalizeFirstLetter(status.replace('_', ' '))}
        </span>
    );

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const renderActionButtons = (report) => (
        <div className="action-buttons">
            <button 
                className="delete-btn" 
                onClick={() => setToRemove(report)}
                disabled={isDeleting || report.status === 'deleted'}
            >
                {isDeleting && toRemove?.news_id === report.news_id ? 'Deleting...' : 'Delete News'}
            </button>
            <button 
                className="edit-btn" 
                onClick={() => setSelected(report)} 
                disabled={report.status !== 'under review' || isDeleting}
            >
                Mark as Not Violating
            </button>
        </div>
    );

    const handleRemoveNews = async () => {
        try {
            setIsDeleting(true);
            const response = await fetchAdminDeleteNews(toRemove.news_id);
            
            if (response.success) {
                dispatch(manageNewsReport(toRemove.news_id, 'delete'));
            }
        } catch (error) {
            // Silent error handling - user can see the news wasn't deleted
        } finally {
            setIsDeleting(false);
            setToRemove(null);
        }
    };

    const handleNotVio = async () => {
        try {
            const response = await fetchAdminMarkNotViolating(selected.news_id);
            if (response.success) {
                dispatch(manageNewsReport(selected.news_id, 'not_violating'));
            }
        } catch (error) {
            // Silent error handling - user can see the status wasn't updated
        }
        setSelected(null);
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
        setCurrentPage(1); // Reset to first page when searching
    };

    const matchesSearchQuery = (report, query) => {
        return (
            report.report_id?.toString().includes(query) ||
            report.reporter_id?.toString().includes(query) ||
            report.news_id?.toString().includes(query) ||
            report.reason?.toLowerCase().includes(query) ||
            report.status?.toLowerCase().includes(query) ||
            moment(report.created_at).format("DD-MM-YYYY").includes(query) ||
            report.news?.title?.toLowerCase().includes(query) ||
            report.reporter?.firstName?.toLowerCase().includes(query) ||
            report.reporter?.lastName?.toLowerCase().includes(query) ||
            report.reporter?.userName?.toLowerCase().includes(query)
        );
    };

    if (loading) {
        return <div className="hyk-table">Loading reported news...</div>;
    }

    if (!newsReports || newsReports.length === 0) {
        return (
            <div className="hyk-table">
                <div className="error-message" style={{ padding: '20px', textAlign: 'center' }}>
                    <h3>No reported news found</h3>
                    <p>This could be because:</p>
                    <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                        <li>No news has been reported yet</li>
                        <li>Backend database relationship issue (check console)</li>
                        <li>API endpoint needs to be configured</li>
                    </ul>
                </div>
            </div>
        );
    }

    return (
        <div className="reported-news-container">

            <div className="hyk-table">
                <table>
                    <thead>
                        <tr>
                            <th onClick={() => handleSort("report_id")} className='c-pointer sortable-header'>
                                Report ID {getSortIcon("report_id")}
                            </th>
                            <th onClick={() => handleSort("created_at")} className='c-pointer sortable-header'>
                                Reported Date {getSortIcon("created_at")}
                            </th>
                            <th onClick={() => handleSort("reporter_name")} className='c-pointer sortable-header'>
                                Reporter {getSortIcon("reporter_name")}
                            </th>
                            <th onClick={() => handleSort("news_title")} className='c-pointer sortable-header'>
                                News Title {getSortIcon("news_title")}
                            </th>
                            <th onClick={() => handleSort("reason")} className='c-pointer sortable-header'>
                                Reason {getSortIcon("reason")}
                            </th>
                            <th onClick={() => handleSort("status")} className='c-pointer sortable-header'>
                                Status {getSortIcon("status")}
                            </th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.map(report => (
                            <tr key={report.report_id}>
                                <td>{report.report_id}</td>
                                <td>{moment(report.created_at).format("DD-MM-YYYY")}</td>
                                <td>
                                    {report.reporter ? 
                                        `${report.reporter.firstName} ${report.reporter.lastName}` : 
                                        report.reporter_id
                                    }
                                </td>
                                <td>
                                    {report.news ? 
                                        <a 
                                            href={`/news/${report.news_id}`} 
                                            className="creation-link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {report.news.title}
                                        </a> : 
                                        report.news_id
                                    }
                                </td>
                                <td>{report.reason}</td>
                                <td>{renderStatus(report.status)}</td>
                                <td>{renderActionButtons(report)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="pagination">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>

                    <span>Page {currentPage} of {totalPages}</span>

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>

                {selected && (
                    <NotVioModal
                        item_name={selected.news?.title || `News ID: ${selected.news_id}`}
                        onClose={() => setSelected(null)}
                        onConfirm={handleNotVio}
                    />
                )}

                {toRemove && (
                    <DeleteConfirmation
                        item_name={`news (ID: ${toRemove.news_id})`}
                        item_type="news"
                        onClose={() => !isDeleting && setToRemove(null)}
                        onConfirm={handleRemoveNews}
                        isLoading={isDeleting}
                    />
                )}
            </div>
        </div>
    );
};

export default ReportedNews;